// ════════════════════════════════════════════════════════════════════════
//  Die Fassungsleiste: ein Ort für den Zustand des Lehrgangs
//
//  Sie hat zwei Gestalten, und der Unterschied zwischen ihnen ist der zentrale
//  Zustand der ganzen App:
//
//    Fassung 13 · veröffentlicht 12.08.2026        [Neuer Entwurf]   ← nur lesen
//    Entwurf · Fassung 14 (aus 13) · gespeichert   [Speichern] […]   ← ändern
//
//  Vorher gab es ohne Entwurf gar nichts zu sehen: „Kein Entwurf offen“ und ein
//  Knopf. Dabei existiert der Lehrgang ja – und nachsehen, was in 4c steht, ist
//  der häufigste Grund, diese App zu öffnen. Jetzt liegt er immer da, nur eben
//  schreibgeschützt, bis jemand einen Entwurf anlegt.
//
//  Vorgänge stehen ausschließlich hier: Anlegen, Speichern, Veröffentlichen,
//  Verwerfen. Ein Vorgang, ein Knopf, ein Ort. Kein Autospeichern.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@otto/plattform";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { useCourseSlug } from "../../../shared/lib/coursePath.js";
import { whoStarted } from "../../../shared/lib/people.js";
import { useEditorialPeople } from "../../../shared/hooks/useEditorialPeople.js";
import { problemTarget } from "../../validation/lib/problemTarget.js";
import { publishGate } from "../lib/publishGate.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./draft.module.css";

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/**
 * @param title Der Name des Lehrgangs. Er steht mit in der Leiste, weil eine
 *              nackte „Fassung 13“ mehrdeutig geworden ist: die Nummern zählen
 *              je Lehrgang, und zwei Lehrgänge haben beide eine Fassung 13.
 */
export function VersionBar({ title }: { title: string }) {
  const slug = useCourseSlug();
  const { client, state } = useSession();
  const people = useEditorialPeople(client);
  const {
    loading,
    bundle,
    draft,
    create,
    creating,
    latestPublished,
    dirty,
    save,
    saving,
    discard,
    problems,
    publish,
    publishing,
    publishError,
    publishedBundle,
    recheck,
  } = useDraft();
  const [asking, setAsking] = useState<"publish" | "discard" | null>(null);

  // Wer den offenen Entwurf angefangen hat. Ein Lehrgang hat höchstens einen -
  // wer vor einem fremden steht, muss wissen, wen er fragt.
  const wer = whoStarted(
    draft?.createdBy ?? null,
    state.kind === "signedIn" ? state.identity.user.id : "",
    people.data,
  );

  if (loading) return null;

  // ── Nur lesen: die letzte veröffentlichte Fassung ────────────────────
  if (!draft) {
    return (
      <section className={own.bar} aria-label="Fassungsleiste">
        <div className={own.barState}>
          <strong>
            {latestPublished
              ? `${title} · Fassung ${latestPublished.version}`
              : `${title} · noch keine Fassung`}
          </strong>
          <span className={own.barFrom}>
            {latestPublished?.publishedAt
              ? `veröffentlicht ${dateFormat.format(new Date(latestPublished.publishedAt))} · unveränderlich`
              : "Der erste Entwurf steht bereit, sobald Sie ihn anlegen."}
          </span>
        </div>

        {/* Die Marke sitzt am Vorgang, den es hier WIRKLICH gibt: ohne Entwurf
            ist dies der einzige Knopf der Leiste. „Veröffentlichen“ gibt es in
            diesem Zustand gar nicht – ein Ring um ein fehlendes Bedienelement
            zeigte auf nichts. */}
        <div className={own.barActions} data-tour="neuer-entwurf">
          <button
            type="button"
            className={styles.button}
            disabled={creating}
            onClick={create}
          >
            {creating
              ? "Wird angelegt …"
              : latestPublished
                ? "Neuer Entwurf"
                : "Ersten Entwurf anlegen"}
          </button>
        </div>
      </section>
    );
  }

  // ── Ändern: der offene Entwurf ───────────────────────────────────────
  const gate = publishGate(problems.length, dirty);

  return (
    <section className={own.bar} aria-label="Fassungsleiste">
      <div className={own.barState}>
        <strong>
          {title} · Entwurf · Fassung {draft.version}
        </strong>
        <span className={own.barFrom}>
          {latestPublished
            ? `aus Fassung ${latestPublished.version}`
            : "erste Fassung des Lehrgangs"}
          {wer !== null && ` · angefangen von ${wer}`}
        </span>
      </div>

      <span className={styles.status}>
        {dirty ? (
          <>
            <span className={styles.dotUnsaved} />
            nicht gespeichert
          </>
        ) : (
          <>
            <span className={styles.dotSaved} />
            gespeichert
          </>
        )}
      </span>

      {/* Die Zahl ist der Weg zum ersten Hinweis. Eine Zahl, die nur zählt,
          lässt einen suchen - und gesucht wird über vier Seiten. */}
      {problems[0] && (
        <Link
          className={styles.hintCount}
          to={problemTarget(problems[0], bundle, slug, publishedBundle).to}
        >
          {problems.length === 1 ? "1 Hinweis" : `${problems.length} Hinweise`}
        </Link>
      )}

      <div className={own.barActions}>
        {asking === "publish" ? (
          <>
            <span className={own.warning}>
              Fassung {draft.version} veröffentlichen? Danach unveränderlich.
            </span>
            <button
              type="button"
              className={styles.button}
              disabled={publishing}
              onClick={() => {
                publish();
                setAsking(null);
              }}
            >
              Ja, veröffentlichen
            </button>
            <button
              type="button"
              className={styles.buttonQuiet}
              onClick={() => setAsking(null)}
            >
              Abbrechen
            </button>
          </>
        ) : asking === "discard" ? (
          <>
            {/* Vorher verwarf der Knopf ohne Rückfrage. Solange er auf einer
                eigenen Seite stand, war der Weg dorthin die Rückfrage; in einer
                Leiste, die überall mitläuft, ist er es nicht mehr. */}
            <span className={own.warning}>
              Entwurf verwerfen? Alles daran Gearbeitete ist weg.
            </span>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                discard();
                setAsking(null);
              }}
            >
              Ja, verwerfen
            </button>
            <button
              type="button"
              className={styles.buttonQuiet}
              onClick={() => setAsking(null)}
            >
              Abbrechen
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.buttonQuiet}
              disabled={saving || !dirty}
              onClick={save}
            >
              {saving ? "Speichert …" : dirty ? "Speichern" : "Gespeichert"}
            </button>
            {/* Hier am Knopf selbst und nicht an der Leiste: er ist bei offenen
                Hinweisen abgeschaltet, aber er STEHT da, und genau das soll die
                Station zeigen – dass Veröffentlichen an einer Bedingung hängt. */}
            <button
              type="button"
              className={styles.button}
              data-tour="veroeffentlichen"
              disabled={!gate.allowed}
              title={gate.title}
              onClick={() => {
                // Erst nachsehen, dann fragen: hat inzwischen ein Kind an etwas
                // Entferntem geübt, schaltet der Hinweis den Knopf ab, statt
                // dass die Datenbank nach dem Ja absagt.
                recheck();
                setAsking("publish");
              }}
            >
              {gate.label}
            </button>
            <button
              type="button"
              className={styles.buttonQuiet}
              onClick={() => setAsking("discard")}
            >
              Verwerfen
            </button>
          </>
        )}
      </div>

      {publishError && (
        <div className={`${styles.notice} ${styles.noticeError} ${own.barError}`}>
          {publishError}
        </div>
      )}
    </section>
  );
}
